"use client";
import { getCookieSettings, saveCookieSettings } from "@/app/[locale]/legal/cookies/actions";
import { localStorageGetCookieSettings, localStorageSaveCookieSettings } from "@/app/components/CookieSetting";
import { usePathname } from "@/i18n/routing";
import { Telegram as TelegramIcon } from "@mui/icons-material";
import { Box, Button, Container, IconButton, Typography } from "@mui/material";
import { Link as NextLink } from "@/i18n/routing"; // 将Next.js的Link重命名
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import XIcon from "./icons/XIcon";
import Logo from "./Logo";

export default function Footer() {
  const t = useTranslations();
  const pathname = usePathname();
  const [cookieConsent, setCookieConsent] = useState<boolean | null>(false);

  useEffect(() => {
    const fetchCookieSettings = async () => {
      const settings = await getCookieSettings();
      if (settings !== null) {
        setCookieConsent(settings.performance || settings.functional || settings.advertising);
      } else {
        const localConsent = localStorageGetCookieSettings();
        if (localConsent !== null) {
          setCookieConsent(localConsent.performance || localConsent.functional || localConsent.advertising);
        } else {
          setCookieConsent(null);
        }
      }
    };

    fetchCookieSettings();
  }, []);

  const handleAcceptCookies = async () => {
    const acceptedSettings = {
      performance: true,
      functional: true,
      advertising: true,
    };
    localStorageSaveCookieSettings(acceptedSettings);
    setCookieConsent(true);
    await saveCookieSettings(acceptedSettings);
  };

  const handleRejectCookies = async () => {
    const rejectedSettings = {
      performance: false,
      functional: false,
      advertising: false,
    };
    localStorageSaveCookieSettings(rejectedSettings);
    setCookieConsent(false);
    await saveCookieSettings(rejectedSettings);
  };

  // 创建通用的链接样式
  const linkSx = {
    color: "inherit",
    textDecoration: "none",
    opacity: 0.8,
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 1,
    },
  };

  const legalLinkSx = {
    ...linkSx,
    flex: { xs: "0 0 calc(50%)", sm: "0 0 auto" }, // xs 下占50%宽度，但留出间距
    fontSize: "var(--font-size-xs)",
    mb: { xs: 1, sm: 0 }, // 在xs下添加底部边距
    textAlign: "left", // 所有屏幕尺寸都左对齐
    display: "block", // 确保链接是块元素
  };

  return (
    <Box
      sx={{
        bgcolor: "background.footer",
        color: "text.footer",
      }}>
      <Container maxWidth="lg">
        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: { xs: 0, md: 4 },
            py: 6,
          }}>
          {/* Logo Section */}
          <Box sx={{ width: { md: "33.333%" } }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
              <Logo color="white" />
              <Typography variant="h6" sx={{ fontFamily: "var(--font-montserrat)", fontWeight: "600" }}>
                Right Pulse
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                bgcolor: "background.footerTagline",
                px: 2,
                py: 1,
                borderRadius: 1,
                display: "inline-block",
              }}>
              {t("Footer.tagline")}
            </Typography>
          </Box>

          {/* Right Side Sections */}
          <Box
            sx={{
              display: "flex",
              width: { md: "66.666%" },
              justifyContent: { md: "flex-end" },
              gap: { xs: 0, sm: 6 }, // 增加列间距
              mt: {xs: 4, sm: 0}, // 在小屏幕上增加顶部间距
            }}>
            {/* About Section */}
            <Box sx={{ 
              flex: { xs: 1, sm: "0 0 auto" },  // 修改flex值,让sm有足够空间
              whiteSpace: { sm: "nowrap" } // 防止文本在sm断点换行
            }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "600",
                  color: "text.footerLight1",
                  mb: 1.2,
                }}>
                {t("Footer.about.title")}
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  "& li": {
                    my: 1,
                  },
                }}>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/about" 
                    sx={linkSx}
                  >
                    {t("Footer.about.aboutUs")}
                  </Box>
                </Box>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/legal/contact" 
                    sx={linkSx}
                  >
                    {t("Footer.about.contactUs")}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Products Section */}
            <Box sx={{ 
              pr: { md: 0 }, 
              flex: { xs: 1, sm: "0 0 auto" },  // 同样修改Products Section的flex
              whiteSpace: { sm: "nowrap" } // 防止文本在sm断点换行
            }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "600",
                  color: "text.footerLight1",
                  mb: 1.2,
                }}>
                {t("Footer.products.title")}
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  "& li": {
                    my: 1,
                  },
                }}>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/research" 
                    sx={linkSx}
                  >
                    {t("Navbar.research")}
                  </Box>
                </Box>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/products/analytics" 
                    sx={linkSx}
                  >
                    {t("Navbar.signals")}
                  </Box>
                </Box>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/products/citadel" 
                    sx={linkSx}
                  >
                    {t("Navbar.citadel")}
                  </Box>
                </Box>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/products/miningHub" 
                    sx={linkSx}
                  >
                    {t("Navbar.miningHub")}
                  </Box>
                </Box>
                <Box component="li">
                  <Box 
                    component={NextLink} 
                    href="/membership" 
                    sx={linkSx}
                  >
                    {t("Navbar.membership")}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Legal Links */}
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", sm: "nowrap" }, // 在xs下启用换行
            justifyContent: "flex-start", // 所有屏幕尺寸都左对齐
            gap: { xs: 0, sm: 4 }, // 调整间距，xs下也有适当间距
            py: 2,
          }}>
          <Box 
            component={NextLink} 
            href="/legal/privacy" 
            sx={legalLinkSx}
          >
            {t("Footer.links.privacy")}
          </Box>
          <Box 
            component={NextLink} 
            href="/legal/terms" 
            sx={legalLinkSx}
          >
            {t("Footer.links.terms")}
          </Box>
          <Box 
            component={NextLink} 
            href="/legal/cookies" 
            sx={legalLinkSx}
          >
            {t("Footer.links.cookies")}
          </Box>
          <Box 
            component={NextLink} 
            href="/legal/disclaimer" 
            sx={legalLinkSx}
          >
            {t("Footer.links.disclaimer")}
          </Box>
        </Box>

        {/* Bottom Section */}
        <Box
          sx={{
            borderTop: 1,
            borderColor: "text.footerLight2",
            pt: 2,
            pb: {xs: 10, sm: 11},
          }}>
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2
          }}>
            <Box sx={{ 
              display: "flex", 
              flexDirection: {xs: "column", sm: "row"},
              justifyContent: "space-between",
              alignItems: {xs: "flex-start", sm: "center"},
              width: "100%",
              gap: 1.5
            }}>
              <Typography variant="body2" sx={{ color: "text.footerLight3" }}>
                {t("Footer.copyright")}
              </Typography>
              <Box sx={{ display: "flex", gap: {xs: 1, sm: 3} }}>
                <IconButton
                  href="https://x.com/RealRightPulse"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "text.footerLight3",
                    "&:hover": {
                      bgcolor: "text.footerLight1",
                    },
                    "& svg": {
                      width: 16,
                      height: 16,
                      color: "background.footer", // 使用footer背景色作为图标色
                    },
                  }}>
                  <XIcon />
                </IconButton>
                <IconButton
                  href="https://t.me/+WYPFkf940lQ0MzFl"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "text.footerLight3",
                    "&:hover": {
                      bgcolor: "text.footerLight1",
                    },
                    "& svg": {
                      width: 16,
                      height: 16,
                      color: "background.footer", // 使用footer背景色作为图标色
                    },
                  }}>
                  <TelegramIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Cookie Consent Section */}
        {cookieConsent === null && !pathname.includes("/legal/cookies") && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "background.paper",
              py: 2,
              boxShadow: 3,
              zIndex: 1000,
            }}>
            <Container
              maxWidth="lg"
              sx={{ 
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // 小屏幕上改为垂直布局
                justifyContent: "space-between", 
                alignItems: { xs: "stretch", sm: "center" }, // 小屏幕上左右拉伸
                gap: { xs: 2, sm: 2 }, // 小屏幕上增加垂直间隙
                py: { xs: 1, sm: 0 }, // 小屏幕上增加垂直内边距
              }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "text.primary", 
                  flex: "1 1 auto",
                  textAlign: { xs: "center", sm: "left" }, // 小屏幕上文本居中对齐
                }}>
                {t.rich("Footer.cookieConsent.message", {
                  cookieSettings: (children) => (
                    <Box 
                      component={NextLink} 
                      href="/legal/cookies" 
                      sx={{ 
                        color: "primary.main", 
                        textDecoration: "underline",
                        "&:hover": {
                          color: "primary.dark"
                        }
                      }}
                    >
                      {children}
                    </Box>
                  ),
                })}
              </Typography>
              <Box sx={{ 
                display: "flex", 
                gap: 2, 
                justifyContent: "space-between", // 按钮之间均匀分布
                width: { xs: "100%", sm: "auto" }, // 小屏幕上按钮容器占满宽度
                flexShrink: 0,
              }}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={handleRejectCookies} 
                  sx={{ 
                    textTransform: "none",
                    flex: { xs: 1, sm: "0 0 auto" }, // 小屏幕上占用所有可用空间，其他屏幕保持原有宽度
                    minWidth: { sm: "7rem" }, // 确保在sm以上有最小宽度
                  }}>
                  {t("Footer.cookieConsent.reject")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAcceptCookies}
                  sx={{ 
                    textTransform: "none",
                    flex: { xs: 1, sm: "0 0 auto" }, // 小屏幕上占用所有可用空间，其他屏幕保持原有宽度
                    minWidth: { sm: "7rem" }, // 确保在sm以上有最小宽度
                  }}>
                  {t("Footer.cookieConsent.accept")}
                </Button>
              </Box>
            </Container>
          </Box>
        )}
      </Container>
    </Box>
  );
}
